/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Apps from '@react/react-spectrum/Icon/Apps';
import BadgeIcon from './Badge/BadgeIcon';
import Button from '@react/react-spectrum/Button';
import classNames from 'classnames';
import ColorIcon from './ColorIcon';
import {
  getFullUrl,
  hashToPath,
  updatePath
} from '@exc/url';
import {getOmegaEvent} from '@exc/events';
import {getOrgDisplayName} from './utils/utils';
import Help from '@react/react-spectrum/Icon/Help';
import {HelpPopover} from '@exc/help-center';
import Label from '@react/react-spectrum/Label';
import metrics from '@adobe/exc-app/metrics';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import PropTypes from 'prop-types';
import Rail from '@react/react-spectrum/Icon/Rail';
import React from 'react';
import Search from '@react/react-spectrum/Icon/Search';
import ShellAppSwitcherPopover from './ShellAppSwitcherPopover';
import ShellAvatar from './ShellAvatar';
import ShellOrgSwitcherPopover from './ShellOrgSwitcherPopover';
import ShellOverlayTrigger from './ShellOverlayTrigger';
import ShellUserProfilePopover from './ShellUserProfilePopover';
import ShellWait from './ShellWait';
import ShellWorkspace from './ShellWorkspace';
import Tooltip from '@react/react-spectrum/Tooltip';
import './ShellHeader.scss';

const CoachMark = React.lazy(() => import('@react/react-spectrum/CoachMark'));

/**
 * Experience Cloud Shell Header component. Container for all shell sub-components.
 * @param {Object} <ObjectPattern> Props from parent component.
 * @returns {ReactElement} Markup for ShellHeader component.
 */
class ShellHeader extends React.Component {
  constructor(props) {
    super(props);
    this.metrics = metrics.create('exc.shell.Shell');
    this.state = {
      globalSearch: {open: false},
      pulseWidget: {
        hasNewAnnouncements: false,
        newNotificationsCount: 0,
        opened: false
      }
    };
  }

  /**
   * Handler for opening and closing Help Center menu.
   * Added for specific case of opening Help menu from solution.
   * @param Event Event object.
   * @param boolean Flag for differentiating explicitly closing with onHide callback.
   */
  onToggleHelpCenter = (e, shouldClose) => {
    let helpCenterProps = this.props.helpCenterConfig;
    const {config, selectedTab} = e && e.detail || {};
    if (config && selectedTab) {
      helpCenterProps = Object.assign({}, helpCenterProps, {
        config,
        open: true,
        selectedTab
      });
    } else {
      helpCenterProps = Object.assign({}, helpCenterProps, {
        open: shouldClose ? false : this.state.helpCenterProps && !this.state.helpCenterProps.open
      });
    }
    this.setState({helpCenterProps});
  };

  onToggleGlobalSearch = shouldClose => {
    const {globalSearch} = this.state;
    globalSearch.open = shouldClose ? false : !globalSearch.open;
    this.setState({globalSearch});
  };

  componentDidMount() {
    document.addEventListener('openHelpCenter', this.onToggleHelpCenter);
  }

  componentWillUnmount() {
    document.removeEventListener('openHelpCenter', this.onToggleHelpCenter);
  }

  getTitle(size) {
    // Both titles should always be set as defaults and fallbacks are determined in Core
    let {solutionTitle, solutionShortTitle} = this.props;
    switch (size) {
      case 'S':
        // Grab the first letter of each word separated by space and upper case them
        const acronym = solutionTitle ? solutionTitle.match(/\b(\w)/g).join('').toUpperCase() : '';
        // Short titles / names over 6 characters cause breakages at the highest zoom we support, so default to acronym if name is too long
        return solutionShortTitle.length > 6 ? acronym : solutionShortTitle;
      case 'M':
        return solutionShortTitle;
      default:
        return solutionTitle;
    }
  }

  /**
   * Shows feedback dialog.
   */
  showFeedbackTool = event => {
    const {
      feedback,
      solutionTitle
    } = this.props;
    this.metrics.analytics.trackEvent(getOmegaEvent({
      attributes: {label: feedback.buttonLabel, type: feedback.type},
      element: 'feedback button',
      event,
      widget: {name: 'feedback', type: 'button'}
    }));

    switch (feedback.type) {
      case 'custom':
        break;
      case 'external':
        window.open(feedback.url, '_blank');
        break;
      case 'openFeedback':
        document.dispatchEvent(new CustomEvent('openHelpCenter', {
          detail: {
            config: {
              subject: feedback.subject || solutionTitle || 'Adobe Experience Cloud',
              type: 'BETA_FEEDBACK_SUBMISSION'
            },
            selectedTab: 'feedback'
          }
        }));
        break;
      default:
        return;
    }
  };

  /**
   * Create Aria-label for the Notification/Announcement Button.
   * @returns {string} Label for Pulse notifications and announcements.
   */
  getPulseLabel() {
    const {formatMessage} = this.props;
    const {pulseWidget: {hasNewAnnouncements, newNotificationsCount}} = this.state;
    let label = formatMessage({
      defaultMessage: 'Notifications and Announcements',
      description: 'Label for the Notifications and Announcements Button',
      messageId: 'notification'});
    if (newNotificationsCount) {
      label += `. ${formatMessage({
        defaultMessage: 'unread notifications',
        description: 'Label for unread notifications',
        messageId: 'unread'})} : ${newNotificationsCount} `;
    }
    if (hasNewAnnouncements) {
      label += `. ${formatMessage({
        defaultMessage: 'Has new announcement',
        description: 'New Announcement Label for Notification Button',
        messageId: 'newAnnouncement'})}`;
    }
    return label;
  }

  /**
   * Checks if environment is in devmode using query param.
   * @returns {boolean} Whether or not shell is in devmode.
   */
  isDevmode(getQueryValue) {
    let localConfigs = JSON.parse(window.localStorage.getItem('unifiedShellConfig'));
    let devmodeEnabled = localConfigs && localConfigs.devmodeEnabled === true;
    return getQueryValue('devmode') === 'true' || devmodeEnabled;
  }

  /**
   * Gets proper label based on dev environment.
   * @returns {string} Proper string for dev environment label.
   */
  getEnvironmentLabel(environment, getQueryValue) {
    if (this.isDevmode(getQueryValue)) {
      return 'Dev Mode';
    }

    switch (environment) {
      case 'dev':
        return 'Dev';
      case 'qa':
        return 'QA';
      case 'stage':
        return 'Staging';
      case 'test':
        return 'Test';
    }
  }

  onTogglePulse = status => {
    let pulseState = {...this.state.pulseWidget, opened: status};
    this.setState({pulseWidget: pulseState});
  };

  onPulseCountChanged = (newNotificationsCount, hasNewAnnouncements) => {
    let pulseState = {...this.state.pulseWidget, hasNewAnnouncements, newNotificationsCount};
    this.setState({pulseWidget: pulseState});
  };

  /**
   * We need to support two variants of global Search
   * 1. Searchbar in the center of the header
   * 2. Magnify Icon in Actions bar.
   * Based on the screen size and availability of workspaces,
   * we decide which variant of global search needs to be displayed in the header.
   * @param appId
   * @param globalSearch
   * @param globalSearchProps
   * @param isWorkspacesAvailable
   * @param leftContainer
   * @param size
   * @param locale
   * @param position
   * @returns {JSX.Element|null}
   */
  checkAndReturnUnifiedSearch = ({appId, globalSearch, globalSearchProps, isWorkspacesAvailable, leftContainer, size, locale}, position) => {
    /**
     * If isWorkspacesAvailable we are not sure if there'd be enough space available for the searchbar or not
     * so the ask is to show magnify icon instead.
     * @type {string|*}
     */
    const mode = isWorkspacesAvailable > 0 ? 'S' : size;
    if (!globalSearchProps.enabled || (mode !== 'L' && position !== 'actions') || (mode === 'L' && position !== 'searchbar')) {
      return null;
    }

    return (
      <React.Suspense fallback={null}>
        <globalSearchProps.SearchWidget
          accessToken={globalSearchProps.accessToken}
          appId={appId}
          getFullUrl={getFullUrl}
          hashToPath={hashToPath}
          leftContainer={leftContainer(true)}
          locale={locale}
          onToggleGlobalSearch={this.onToggleGlobalSearch}
          open={globalSearch.open}
          selectedImsOrg={globalSearchProps.selectedImsOrg}
          shellOverlayTrigger={ShellOverlayTrigger}
          size={mode}
          updatePath={updatePath}>
        </globalSearchProps.SearchWidget>
      </React.Suspense>
    );
  };

  render() {
    const {
      appContainer,
      appId,
      coachMark,
      customProfileButtons,
      currentActiveTenantId,
      customEnvLabel,
      customSearch,
      environment,
      feedback,
      formatMessage,
      getQueryValue,
      globalSearch: globalSearchProps,
      hasAccountClusterData,
      helpCenterConfig,
      imsOrgs,
      landingpage,
      locale,
      localeData,
      modal,
      noAccess,
      onCustomProfileButtonClick,
      onImsOrgChange,
      onSignOutClick,
      onSolutionClick,
      orgToSubOrgsMap,
      pulse,
      selectedImsOrg,
      selectedSubOrg,
      selectedWorkspaceId,
      services,
      settingsService,
      sideNavEnabled,
      sideNavOpen,
      size,
      solutionIcon,
      solutions,
      toggleSideNav,
      userAvatar,
      userFirstName,
      userLastName,
      userName,
      userTitle,
      translationService,
      viewportTakeover,
      workspaces
    } = this.props;
    const {globalSearch, helpCenterProps, pulseWidget} = this.state;
    const envLabel = this.getEnvironmentLabel(environment, getQueryValue);
    const PulseContainer = pulse.PulseContainer;
    const PulseWidgetWrapper = pulse.PulseWidgetWrapper;
    const BadgeComponent = <BadgeIcon
      count={pulseWidget.newNotificationsCount}
      hasNewAnnouncement={pulseWidget.hasNewAnnouncements}
      key="pulse-badge"
      maxDigits={1}
    />;

    /**
     * The leftContainer is passed and used by Global Search component,
     * and in case search component we don't need to show workspaces.
     * So the parameter "inSearchPopover" conditionally controls whether the leftContainer
     * should return with workspaces or not.
     * The Global Search component show the left container in an overlay
     * which should be the exact copy(Including icon/text and env label) of what we show in shell header,
     * which is why we pass the left container in search container.
     * @param inSearchPopover
     * @returns {JSX.Element}
     */
    const leftContainer = inSearchPopover => {
      return (
        <div className="spectrum-Shell-leftContainer">
          {sideNavEnabled && (
            <OverlayTrigger delayShow={1200}>
              <Button
                className={classNames('spectrum-Shell-hamburgerMenu-wrapper', {sideNavOpen})}
                onClick={e => toggleSideNav(e)}>
                <Rail className="spectrum-Shell-hamburgerMenu" size="S"/>
              </Button>
              <Tooltip placement="right" variant="neutral">
                {sideNavOpen ? formatMessage({
                  defaultMessage: 'Close menu',
                  description: 'Tool tip label for closing side nav with hamburger menu button',
                  messageId: 'sidenavMenuCloseTooltip'
                }) : formatMessage({
                  defaultMessage: 'Open menu',
                  description: 'Tool tip label for opening side nav with hamburger menu button',
                  messageId: 'sidenavMenuOpenTooltip'
                })}
              </Tooltip>
            </OverlayTrigger>)
          }
          <Button
            aria-labelledby="spectrum-Shell-logoLabel"
            className="spectrum-Shell-logoIcon"
            onClick={onSolutionClick}
            onKeyUp={onSolutionClick}
            variant="tool"
          >
            {solutionIcon && <ColorIcon iconName={solutionIcon} iconSize="M"/>}
            <h1 className="spectrum-Shell-logoLabel" id="spectrum-Shell-logoLabel">
              {this.getTitle(size)}
            </h1>
          </Button>
          {envLabel && (
            <Label
              className={classNames('spectrum-Shell-envLabel', {smallMargin: customEnvLabel})}
              variant="orange"
            >
              {envLabel}
            </Label>
          )}
          {customEnvLabel && (
            <Label className="spectrum-Shell-envLabel" variant="green">
              {customEnvLabel}
            </Label>
          )}
          {!inSearchPopover && <ShellWorkspace selected={selectedWorkspaceId} workspaces={workspaces}/>}
        </div>
      );
    };

    const unifiedSearchProps = {
      appId,
      globalSearch,
      globalSearchProps,
      isWorkspacesAvailable: workspaces.length > 0,
      leftContainer,
      locale,
      size
    };

    const actions = [
      noAccess || (
        <ShellOverlayTrigger
          element="app switcher"
          key="app-switcher"
          placement="bottom left">
          <Button
            aria-label={formatMessage({
              defaultMessage: 'Switch to another Solution',
              description: 'Label for Solution Switcher',
              messageId: 'solutionSwitcher'})}
            className="app-switcher-menu"
            variant="tool">
            <Apps size="S" />
          </Button>
          <ShellAppSwitcherPopover
            currentAppId={appId}
            environment={environment}
            landingpage={landingpage}
            locale={locale}
            services={services}
            settingsService={settingsService}
            solutions={solutions}
          />
        </ShellOverlayTrigger>
      ),
      customSearch.enabled && (
        <span
          className={classNames({'spectrum-Shell-custom-menu--open': customSearch.open})}
          key="custom-search"
        >
          <Button
            aria-label={formatMessage({
              defaultMessage: 'Search',
              description: 'Label for search button',
              messageId: 'searchButton'})}
            icon={<Search />}
            onClick={customSearch.onClick}
            quiet
            selected={customSearch.open}
            variant="tool" />
        </span>
      ),
      this.checkAndReturnUnifiedSearch(unifiedSearchProps, 'actions'),
      noAccess || (
        <ShellOverlayTrigger
          element="help center"
          key="help-menu"
          onHide={e => this.onToggleHelpCenter(e, true)}
          placement="bottom"
          show={helpCenterProps && helpCenterProps.open}
          trigger="click">
          <Button
            aria-label={formatMessage({
              defaultMessage: 'Help',
              description: 'Help header',
              messageId: 'help'})}
            className="help-menu"
            onClick={this.onToggleHelpCenter}
            variant="tool">
            <Help size="S"/>
          </Button>
          <HelpPopover
            {...helpCenterProps || helpCenterConfig}
            ShellWait={ShellWait}
            locale={locale}
            localeData={localeData}
            translationService={translationService} />
        </ShellOverlayTrigger>
      ),
      pulse.enabled && !noAccess ? (
        <PulseContainer
          key="notifications-badge"
          onUnreadChange={this.onPulseCountChanged}
          opened={pulseWidget.opened}
          {...pulseWidget}
          {...pulse}
        >
          <ShellOverlayTrigger
            animateFrom="top"
            element="pulse"
            full
            onHide={() => this.onTogglePulse(false)}
            onShow={() => this.onTogglePulse(true)}
            placement="bottom"
            trigger="click">
            <Button
              aria-label={this.getPulseLabel()}
              className="pulse-menu"
              variant="tool"
            >
              {BadgeComponent}
            </Button>
            <PulseWidgetWrapper onViewAllClicked={pulse.onViewAllNotificationsClickHandler}/>
          </ShellOverlayTrigger>
        </PulseContainer>
      ) : (
        <Button
          aria-label={this.getPulseLabel()}
          className="pulse-menu"
          variant="tool"
        >
          {BadgeComponent}
        </Button>
      ),
      <ShellOverlayTrigger
        element="user profile"
        key="profile-menu"
        placement="bottom">
        <Button
          aria-label={formatMessage({
            defaultMessage: 'Profile',
            description: 'Profile Button Label',
            messageId: 'profile'})}
          className="shell-avatar"
          variant="tool">
          <ShellAvatar src={userAvatar} />
        </Button>
        <ShellUserProfilePopover
          avatarUrl={userAvatar}
          currentActiveTenantId={currentActiveTenantId}
          customProfileButtons={customProfileButtons}
          firstName={userFirstName}
          formatMessage={formatMessage}
          hasAccountClusterData={hasAccountClusterData}
          hasImsOrgSwitcher={size === 'S' || (imsOrgs && imsOrgs.length === 1)}
          imsOrgs={imsOrgs}
          lastName={userLastName}
          locale={locale}
          localeData={localeData}
          name={userName}
          noAccess={noAccess}
          onCustomProfileButtonClick={onCustomProfileButtonClick}
          onImsOrgChange={onImsOrgChange}
          onSignOutClick={onSignOutClick}
          orgToSubOrgsMap={orgToSubOrgsMap}
          selectedImsOrg={selectedImsOrg}
          selectedSubOrg={selectedSubOrg}
          subheading={userTitle}
        />
      </ShellOverlayTrigger>
    ];
    // Include the switcher when:
    // 1. size is not small
    // 2. we have > 1 orgs
    // 3. we have a selected org
    // 4. we have 1 org and > 1 sub orgs
    let hasDifferentAccount = imsOrgs.some(({value}) => value.indexOf('@AdobeOrg') === -1);
    let hasMultipleSubOrgs = orgToSubOrgsMap && imsOrgs[0] && orgToSubOrgsMap[imsOrgs[0].value] && orgToSubOrgsMap[imsOrgs[0].value].length > 1;
    if (size !== 'S' && imsOrgs && (imsOrgs.length > 1 || hasDifferentAccount || hasMultipleSubOrgs) && (selectedImsOrg || noAccess)) {
      actions.unshift(
        <ShellOverlayTrigger
          className="spectrum-Shell-orgMenu"
          element="ims org switcher"
          key="org-menu"
          placement="bottom"
        >
          <Button className="spectrum-Shell-orgButton" variant="tool">
            {noAccess ?
              formatMessage({
                defaultMessage: 'Select another org account',
                description: 'Org switcher item to select another account',
                messageId: 'adobeExperienceCloudNoOrgsSelect'
              }) :
              getOrgDisplayName(imsOrgs, selectedImsOrg, selectedSubOrg, orgToSubOrgsMap)}
          </Button>
          <ShellOrgSwitcherPopover
            hasAccountClusterData={hasAccountClusterData}
            imsOrgs={imsOrgs}
            locale={locale}
            onImsOrgChange={onImsOrgChange}
            orgToSubOrgsMap={orgToSubOrgsMap}
            selectedImsOrg={selectedImsOrg}
            selectedSubOrg={selectedSubOrg}
          />
        </ShellOverlayTrigger>
      );
    }

    if (size !== 'S' && feedback && feedback.enabled) {
      actions.unshift(
        <Button
          aria-label={feedback.buttonLabel}
          className="spectrum-Shell-feedback-button"
          key="feedback-button"
          onClick={this.showFeedbackTool}
          variant="cta"
        >
          <span className="spectrum-Shell-feedback-button">
            {feedback.buttonLabel}
          </span>
        </Button>
      );
    }
    const showCoachMark = coachMark && coachMark.enabled && !coachMark.hidden;
    const showUnderlay = showCoachMark || !appContainer && modal;
    return (
      <header
        aria-hidden={showCoachMark || modal || viewportTakeover}
        className={classNames('spectrum-Shell spectrum--darkest', {'has-appContainer': appContainer, 'modal-open': modal})}>
        <div className={classNames('spectrum-Underlay', {'is-open': showUnderlay})} style={{position: 'absolute'}}></div>
        <div className="spectrum-Shell-headingContainer">
          {showCoachMark &&
            <React.Suspense fallback={null}>
              <CoachMark
                selector={coachMark.element}
                title=""
              />
            </React.Suspense>
          }
          {leftContainer()}
          {this.checkAndReturnUnifiedSearch(unifiedSearchProps, 'searchbar')}
          <div className="spectrum-Shell-rightContainer">{actions}</div>
        </div>
        {this.props.children}
      </header>
    );
  }
}

const NOOP = () => {};

ShellHeader.defaultProps = {
  customSearch: {
    enabled: false,
    onClick: () => {},
    open: false
  },
  feedback: null,
  globalSearch: {},
  helpCenterConfig: {},
  imsOrgs: [],
  locale: 'en-US',
  modal: false,
  noAccess: false,
  onImsOrgChange: NOOP,
  onSignOutClick: NOOP,
  onSolutionClick: NOOP,
  onWorkspaceChange: NOOP,
  pulse: {
    onPulseViewAllClick: NOOP
  },
  selectedWorkspaceId: '',
  sideNavEnabled: false,
  size: 'L',
  solutionShortTitle: 'Adobe Experience Cloud',
  solutionTitle: 'Adobe Experience Cloud',
  toggleSideNav: NOOP,
  userAvatar: undefined,
  userName: 'Adobe User',
  userTitle: undefined,
  viewportTakeover: false,
  workspaces: []
};

ShellHeader.propTypes = {
  customSearch: PropTypes.shape({
    enabled: PropTypes.bool,
    onClick: PropTypes.func,
    open: PropTypes.bool
  }),
  feedback: PropTypes.shape({
    enabled: PropTypes.bool,
    type: PropTypes.string
  }),
  hasAccountClusterData: PropTypes.bool,
  helpCenterConfig: PropTypes.shape({
    resources: PropTypes.arrayOf(
      PropTypes.shape({
        href: PropTypes.string,
        icon: PropTypes.string,
        label: PropTypes.string
      })
    )
  }),
  imsOrgs: PropTypes.arrayOf(
    PropTypes.shape({
      imsOrgId: PropTypes.string,
      imsOrgName: PropTypes.string,
      tenantId: PropTypes.string
    })
  ).isRequired,
  locale: PropTypes.string,
  /** Whether a modal is showing or not */
  modal: PropTypes.bool,
  noAccess: PropTypes.bool,
  onImsOrgChange: PropTypes.func,
  onSignOutClick: PropTypes.func,
  /** Is triggered when the solution name is clicked */
  onSolutionClick: PropTypes.func,
  /** Is triggered when the workspace is changed */
  onWorkspaceChange: PropTypes.func,
  pulse: PropTypes.shape({
    accessToken: PropTypes.boolean,
    enabled: PropTypes.boolean,
    onPulseViewAllClick: PropTypes.func,
    pulseContainer: PropTypes.element

  }),
  selectedImsOrg: PropTypes.string,
  selectedSubOrg: PropTypes.object,
  /** Is triggered when the solution name is clicked */
  selectedWorkspaceId: PropTypes.string,
  settingsService: PropTypes.object,
  sideNavEnabled: PropTypes.bool,
  /** Size variant: S/M/L */
  size: PropTypes.oneOf(['S', 'M', 'L']),
  /** The solution icon or image */
  solutionIcon: PropTypes.string,
  /** The short name of the solution */
  solutionShortTitle: PropTypes.string,
  /** The full name of the solution */
  solutionTitle: PropTypes.string,
  toggleSideNav: PropTypes.func,
  userAvatar: PropTypes.string,
  userFirstName: PropTypes.string,
  userLastName: PropTypes.string,
  userName: PropTypes.string,
  userTitle: PropTypes.string,
  /** Whether there is viewportTakeover or not */
  viewportTakeover: PropTypes.bool,
  /** List of workspaces to show in the left side of the shell */
  workspaces: PropTypes.arrayOf(
    PropTypes.shape({
      /** Size variant: S/M/L */
      name: PropTypes.string,
      /** URL for workspace item */
      url: PropTypes.string
    })
  )
};

export {ShellHeader};
export default React.memo(ShellHeader);
